// src/components/ProjectRates.jsx
// "Ansätze" tab - manage hourly rates per team member per project phase
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getProjectPhases } from "../services/azureProjectPhasesService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { getProjectRates, saveRate, deletePersonRates } from "../services/azureRatesService";
import { findTeamMember } from "../utils/teamLookup";
import "../styles/_project-rates.scss";

const GLOBAL_ID = "_global";

const ProjectRates = ({ project, projectCode, readOnly = false }) => {
  const [phases, setPhases] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [rates, setRates] = useState([]); // [{projectCode, employeeId, phaseId, rate}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Track which cells have been modified (for visual feedback)
  const [dirtyKeys, setDirtyKeys] = useState(new Set());

  useEffect(() => {
    loadData();
  }, [projectCode]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [phasesData, team, ratesData] = await Promise.all([
        getProjectPhases(projectCode),
        getAllTeamMembers(),
        getProjectRates(projectCode),
      ]);
      setPhases(phasesData.sort((a, b) => (a.startDate || "").localeCompare(b.startDate || "")));
      setTeamMembers(team);
      setRates(ratesData);
    } catch (err) {
      console.error("Error loading rates data:", err);
      setError("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  // Persons who have at least one rate entry (excluding _global)
  const assignedPersonIds = useMemo(() => {
    return [...new Set(rates.filter((r) => r.employeeId !== GLOBAL_ID).map((r) => r.employeeId))];
  }, [rates]);

  // Pro Eintrag merken wir uns die Roh-ID (employeeId aus rates) UND den
  // aufgeloesten Member. So koennen verwaiste Eintraege weiterhin angezeigt
  // werden ("Unbekannt (gelöscht)") und Eintraege via teamId/uuid/nummer matchen.
  const assignedMembers = useMemo(() => {
    return assignedPersonIds
      .map((id) => ({ personId: id, member: findTeamMember(teamMembers, id) }))
      .sort((a, b) => {
        const nameA = a.member?.name || "￿";
        const nameB = b.member?.name || "￿";
        return nameA.localeCompare(nameB);
      });
  }, [assignedPersonIds, teamMembers]);

  // Available members to add (not yet assigned). teamId-Match bleibt fuer
  // Altdaten in rates, neue Eintraege werden immer per employeeUuid geschrieben.
  const assignedKeySet = useMemo(() => new Set(assignedPersonIds), [assignedPersonIds]);
  const availableMembers = useMemo(() => {
    return teamMembers
      .filter((tm) => {
        if (!tm.employeeUuid) return false;
        if (
          assignedKeySet.has(tm.employeeUuid) ||
          assignedKeySet.has(tm.teamId)
        ) {
          return false;
        }
        if (!tm.imTeam) return false;
        if (searchQuery) {
          return tm.name?.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [teamMembers, assignedKeySet, searchQuery]);

  const getPersonId = (member) => member.employeeUuid;

  // Get the rate value for a specific person/phase combo
  const getRate = useCallback(
    (employeeId, phaseId) => {
      const entry = rates.find(
        (r) => r.employeeId === employeeId && r.phaseId === phaseId
      );
      return entry ? entry.rate : "";
    },
    [rates]
  );

  // Get the global rate for a phase
  const getGlobalRate = useCallback(
    (phaseId) => {
      const entry = rates.find(
        (r) => r.employeeId === GLOBAL_ID && r.phaseId === phaseId
      );
      return entry ? entry.rate : "";
    },
    [rates]
  );

  // Get effective rate: individual > global > empty
  const getEffectiveRate = useCallback(
    (employeeId, phaseId) => {
      const individual = getRate(employeeId, phaseId);
      if (individual !== "" && individual > 0) return individual;
      return getGlobalRate(phaseId);
    },
    [getRate, getGlobalRate]
  );

  // Handle rate change (local state update)
  const handleRateChange = (employeeId, phaseId, value) => {
    const numericValue = value === "" ? 0 : parseFloat(value);
    if (isNaN(numericValue)) return;

    setRates((prev) => {
      const idx = prev.findIndex(
        (r) => r.employeeId === employeeId && r.phaseId === phaseId
      );
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], rate: numericValue };
        return updated;
      }
      return [
        ...prev,
        { projectCode, employeeId, phaseId, rate: numericValue },
      ];
    });

    setDirtyKeys((prev) => new Set(prev).add(`${employeeId}_${phaseId}`));
  };

  // Save a single rate on blur
  const handleRateBlur = async (employeeId, phaseId) => {
    const key = `${employeeId}_${phaseId}`;
    if (!dirtyKeys.has(key)) return;

    const entry = rates.find(
      (r) => r.employeeId === employeeId && r.phaseId === phaseId
    );
    const rate = entry?.rate || 0;

    try {
      await saveRate(projectCode, employeeId, phaseId, rate);
      setDirtyKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } catch (err) {
      console.error("Error saving rate:", err);
      setError("Fehler beim Speichern");
    }
  };

  // Add a person with empty rates
  const handleAddPerson = async (member) => {
    const employeeId = getPersonId(member);
    try {
      await saveRate(projectCode, employeeId, "default", 0);
      setRates((prev) => [
        ...prev,
        { projectCode, employeeId, phaseId: "default", rate: 0 },
      ]);
      setShowAddPerson(false);
      setSearchQuery("");
    } catch (err) {
      console.error("Error adding person:", err);
      setError("Fehler beim Hinzufügen");
    }
  };

  // Remove a person (delete all their rates for this project)
  const handleRemovePerson = async (employeeId) => {
    if (!confirm("Person und alle zugehörigen Ansätze entfernen?")) return;
    try {
      await deletePersonRates(projectCode, employeeId);
      setRates((prev) => prev.filter((r) => r.employeeId !== employeeId));
    } catch (err) {
      console.error("Error removing person:", err);
      setError("Fehler beim Entfernen");
    }
  };

  // Copy default rate to all phases for a person (or global)
  const handleCopyDefaultToPhases = async (employeeId) => {
    const defaultRate = getRate(employeeId, "default");
    if (!defaultRate) return;

    setSaving(true);
    try {
      const updates = phases.map((phase) => ({
        employeeId,
        phaseId: phase.id,
        rate: defaultRate,
      }));

      await Promise.all(
        updates.map((u) => saveRate(projectCode, u.employeeId, u.phaseId, u.rate))
      );

      setRates((prev) => {
        let updated = [...prev];
        for (const u of updates) {
          const idx = updated.findIndex(
            (r) => r.employeeId === u.employeeId && r.phaseId === u.phaseId
          );
          if (idx >= 0) {
            updated[idx] = { ...updated[idx], rate: u.rate };
          } else {
            updated.push({ projectCode, ...u });
          }
        }
        return updated;
      });
    } catch (err) {
      console.error("Error copying rates:", err);
      setError("Fehler beim Kopieren");
    } finally {
      setSaving(false);
    }
  };

  const getMemberName = (member) => member?.name || "Unbekannt (gelöscht)";
  const getMemberCategory = (member) => member?.kategorie || "";

  // Check if an individual rate overrides the global one
  const hasIndividualRate = (employeeId, phaseId) => {
    const individual = getRate(employeeId, phaseId);
    return individual !== "" && individual > 0;
  };

  if (loading) {
    return <div className="project-rates loading-state">Daten werden geladen...</div>;
  }

  return (
    <div className="project-rates">
      {error && (
        <div className="error-banner" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      <div className="rates-header">
        <h3>Ansätze (Stundensätze)</h3>
        <p className="rates-subtitle">
          Stundensätze pro Projektphase festlegen. Globale Ansätze gelten für alle Teammitglieder.
          Individuelle Ansätze pro Person überschreiben den globalen Wert.
        </p>
      </div>

      {phases.length === 0 && (
        <div className="empty-info">
          Keine Projektphasen vorhanden. Bitte zuerst Phasen im Controlling erfassen.
        </div>
      )}

      <div className="rates-table-wrapper">
        <table className="rates-table">
          <thead>
            <tr>
              <th className="person-col">Teammitglied</th>
              <th className="rate-col default-col">
                Standard
                <span className="col-hint">Fallback</span>
              </th>
              {phases.map((phase) => (
                <th key={phase.id} className="rate-col phase-col">
                  <span className="phase-name">{phase.name}</span>
                  {phase.startDate && phase.endDate && (
                    <span className="phase-dates">
                      {formatDate(phase.startDate)} - {formatDate(phase.endDate)}
                    </span>
                  )}
                </th>
              ))}
              {!readOnly && <th className="actions-col"></th>}
            </tr>
          </thead>
          <tbody>
            {/* Global rate row */}
            <tr className="global-row">
              <td className="person-col">
                <div className="person-cell">
                  <span className="person-name global-label">Globaler Ansatz</span>
                  <span className="person-category">gilt für alle</span>
                </div>
              </td>
              <td className="rate-col default-col">
                <div className="rate-input-wrapper">
                  {readOnly ? (
                    <span className="rate-display">
                      {getRate(GLOBAL_ID, "default") || "-"}
                    </span>
                  ) : (
                    <>
                      <input
                        type="number"
                        min="0"
                        step="5"
                        className="rate-input global-input"
                        value={getRate(GLOBAL_ID, "default") || ""}
                        onChange={(e) =>
                          handleRateChange(GLOBAL_ID, "default", e.target.value)
                        }
                        onBlur={() => handleRateBlur(GLOBAL_ID, "default")}
                        placeholder="0"
                      />
                      {getRate(GLOBAL_ID, "default") > 0 && phases.length > 0 && (
                        <button
                          className="copy-btn"
                          title="Standard auf alle Phasen kopieren"
                          onClick={() => handleCopyDefaultToPhases(GLOBAL_ID)}
                          disabled={saving}
                        >
                          &rarr;
                        </button>
                      )}
                    </>
                  )}
                </div>
              </td>
              {phases.map((phase) => (
                <td key={phase.id} className="rate-col phase-col">
                  {readOnly ? (
                    <span className="rate-display">
                      {getGlobalRate(phase.id) || "-"}
                    </span>
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step="5"
                      className="rate-input global-input"
                      value={getGlobalRate(phase.id) || ""}
                      onChange={(e) =>
                        handleRateChange(GLOBAL_ID, phase.id, e.target.value)
                      }
                      onBlur={() => handleRateBlur(GLOBAL_ID, phase.id)}
                      placeholder="0"
                    />
                  )}
                </td>
              ))}
              {!readOnly && <td className="actions-col"></td>}
            </tr>

            {/* Separator */}
            {assignedMembers.length > 0 && (
              <tr className="separator-row">
                <td colSpan={phases.length + (readOnly ? 2 : 3)}>
                  <div className="separator-label">Individuelle Ansätze (überschreiben global)</div>
                </td>
              </tr>
            )}

            {assignedMembers.map(({ personId, member }) => {
              const employeeId = personId;
              const isOrphan = !member;
              return (
                <tr key={employeeId} className={isOrphan ? "orphan-row" : undefined}>
                  <td className="person-col">
                    <div className="person-cell" title={isOrphan ? `ID: ${employeeId}` : undefined}>
                      <span className="person-name">{getMemberName(member)}</span>
                      <span className="person-category">{getMemberCategory(member)}</span>
                    </div>
                  </td>
                  <td className="rate-col default-col">
                    <div className="rate-input-wrapper">
                      {readOnly ? (
                        <span className="rate-display">
                          {getRate(employeeId, "default") || "-"}
                        </span>
                      ) : (
                        <>
                          <input
                            type="number"
                            min="0"
                            step="5"
                            className="rate-input"
                            value={getRate(employeeId, "default") || ""}
                            onChange={(e) =>
                              handleRateChange(employeeId, "default", e.target.value)
                            }
                            onBlur={() => handleRateBlur(employeeId, "default")}
                            placeholder={getGlobalRate("default") || "0"}
                          />
                          {getRate(employeeId, "default") > 0 && phases.length > 0 && (
                            <button
                              className="copy-btn"
                              title="Standard auf alle Phasen kopieren"
                              onClick={() => handleCopyDefaultToPhases(employeeId)}
                              disabled={saving}
                            >
                              &rarr;
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  {phases.map((phase) => {
                    const individual = getRate(employeeId, phase.id);
                    const global = getGlobalRate(phase.id);
                    const isOverride = individual !== "" && individual > 0;
                    return (
                      <td key={phase.id} className={`rate-col phase-col${isOverride ? " has-override" : ""}`}>
                        {readOnly ? (
                          <span className="rate-display">
                            {individual || global || "-"}
                          </span>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="5"
                            className={`rate-input${isOverride ? " override" : ""}`}
                            value={individual || ""}
                            onChange={(e) =>
                              handleRateChange(employeeId, phase.id, e.target.value)
                            }
                            onBlur={() => handleRateBlur(employeeId, phase.id)}
                            placeholder={global || "0"}
                          />
                        )}
                      </td>
                    );
                  })}
                  {!readOnly && (
                    <td className="actions-col">
                      <button
                        className="remove-btn"
                        title="Person entfernen"
                        onClick={() => handleRemovePerson(employeeId)}
                      >
                        &times;
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="add-person-section">
          {showAddPerson ? (
            <div className="add-person-dropdown">
              <input
                type="text"
                className="search-input"
                placeholder="Name suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <div className="person-list">
                {availableMembers.length === 0 && (
                  <div className="no-results">Keine weiteren Teammitglieder verfügbar</div>
                )}
                {availableMembers.map((member) => (
                  <div
                    key={getPersonId(member)}
                    className="person-option"
                    onClick={() => handleAddPerson(member)}
                  >
                    <span className="person-name">{getMemberName(member)}</span>
                    <span className="person-category">{getMemberCategory(member)}</span>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddPerson(false);
                  setSearchQuery("");
                }}
              >
                Abbrechen
              </button>
            </div>
          ) : (
            <button
              className="btn btn-primary add-person-btn"
              onClick={() => setShowAddPerson(true)}
            >
              + Individuellen Ansatz hinzufügen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const [y, m, d] = dateStr.split("-");
    return `${d}.${m}.${y}`;
  } catch {
    return dateStr;
  }
}

export default ProjectRates;
